import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Description as DescriptionIcon,
  Source as SourceIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Attachment as AttachmentIcon,
  DevicesOther as DevicesOtherIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Language as LanguageIcon,
  MiscellaneousServices as MiscellaneousServicesIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useDispatch } from 'react-redux';
import { getLeadById, downloadLeadFile, getLeadsStatus, updateLeadStatus } from '../../store/slices/leadsSlice';
import { getUserByMultiRoles } from '../../store/slices/userSlice';
import { toast } from 'react-toastify';
import { MEETING_EDIT_PATH, MEETING_VIEW_PATH } from '../../navigations/path';
import { extractList } from '../../utils/response';
import StatusDialog from '../../components/Leads/dialogs/StatusDialog';
import Can from '../../components/permissions/Can';
import { useAuth } from '../../hooks/useAuth';
import {
  isQcOrQcManagerRole,
  isQcApprovedOrDeclinedStatus,
  isLeadDisabledLike,
} from '../../components/Leads/utils';

const displayOrNa = (value) =>
  value !== undefined && value !== null && value !== '' ? value : 'N/A';

const formatBundledDisplay = (raw) => {
  if (raw === true || raw === 1 || raw === 'Yes' || raw === 'yes') return 'Yes';
  if (raw === false || raw === 0 || raw === 'No' || raw === 'no') return 'No';
  return 'N/A';
};

const DbaDetailField = ({ label, value, md = 6 }) => (
  <Grid item xs={12} md={md}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {displayOrNa(value)}
    </Typography>
  </Grid>
);

const LeadDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, hasPermission } = useAuth();
  const canViewAttachment = hasPermission('attachment-view');
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [meetingTab, setMeetingTab] = useState(0);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [frontSellerOptions, setFrontSellerOptions] = useState([]);
  const [newStatus, setNewStatus] = useState('');
  const [statusProcessing, setStatusProcessing] = useState(false);
  const [conditionalData, setConditionalData] = useState({
    date: '',
    description: '',
    assignees: [],
    equipment_model_id: '',
    lease_id: '',
    merchant_service_provider_id: '',
    timeZone: 'UTC',
    startDateTime: '',
    endDateTime: '',
    meetingDescription: '',
  });

  const getCurrentDatetimeLocalValue = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  useEffect(() => {
    fetchLeadDetail();
  }, [id]);

  useEffect(() => {
    setMeetingTab(0);
  }, [id]);

  useEffect(() => {
    fetchStatusMeta();
  }, []);

  const fetchLeadDetail = async () => {
    try {
      setLoading(true);
      const res = await dispatch(getLeadById(id)).unwrap();
      const leadData = res?.data ?? res;
      if (leadData) {
        setLead(leadData);

      } else {
        toast.error('Failed to fetch lead details');
        navigate('/leads');
      }
    } catch (error) {
      toast.error('Error loading lead details');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusMeta = async () => {
    try {
      const [statusRes, usersRes] = await Promise.all([
        dispatch(getLeadsStatus({ 'conditions[model]': 'Lead' })).unwrap(),
        dispatch(getUserByMultiRoles({ 'roles[]': ['Closer', 'Lead Gen'], paginate: 0 })).unwrap(),
      ]);
      const statusList = extractList(statusRes);
      const usersList = extractList(usersRes?.data ?? usersRes).map((u) => ({
        id: u?.uuid ?? u?.id,
        name: u?.name,
      }));
      setStatusOptions(statusList);
      setFrontSellerOptions(usersList);
    } catch (error) {
      toast.error('Failed to load status options');
    }
  };

  const handleBack = () => {
    navigate('/leads');
  };

  const handleImageClick = (file) => {
    setSelectedImage(file?.path);
    setSelectedImageFile(file);
    setOpenImageDialog(true);
  };

  const handleDownloadFile = async (file) => {
    const filePath = file?.path;
    const fileName = file?.title || file?.file_name || file?.name || 'download';
    if (!filePath) {
      toast.error('No file path available for download');
      return;
    }
    try {
      setDownloadingAttachmentId(file?.id ?? filePath);
      const res = await dispatch(downloadLeadFile(filePath)).unwrap();
      const blob = new Blob([res.data], {
        type: res.headers?.['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${fileName} downloaded`);
    } catch (error) {
      toast.error(error?.message || 'Failed to download file');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const toDatetimeLocalValue = (value) => {
    if (value == null || value === '') return '';
    const d = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const resolveLeadStatusId = (item) => {
    if (!item) return '';
    const raw =
      item.last_status_log?.status?.id ??
      item.status?.status?.id ??
      item.status?.id ??
      item.status_id;
    if (raw === undefined || raw === null || raw === '') return '';
    return raw;
  };

  const matchStatusId = (list, rawId) => {
    if (rawId === '' || rawId == null) return '';
    const found = (list || []).find(
      (s) => s.id === rawId || String(s.id) === String(rawId)
    );
    return found ? found.id : rawId;
  };

  const handleOpenStatusDialog = () => {
    if (!lead) return;
    const rawStatusId = resolveLeadStatusId(lead);
    const statusId = matchStatusId(statusOptions, rawStatusId);
    const log = lead?.last_status_log;
    const meeting = log?.meeting;
    const statusName = log?.status?.name || lead?.status?.status?.name || lead?.status?.name;
    const assigneeIdsFromLead =
      lead?.assignees?.map((a) => a.id ?? a.uuid).filter(Boolean) ?? [];

    const nextConditionalData = {
      date: getCurrentDatetimeLocalValue(),
      description: '',
      assignees: [],
      equipment_model_id:
        lead?.equipment_model_id ?? lead?.equipment_model?.id ?? '',
      lease_id: lead?.lease_id ?? lead?.lease?.id ?? '',
      merchant_service_provider_id:
        lead?.merchant_service_provider_id ??
        lead?.merchant_service_provider?.id ??
        '',
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
    };

    if (statusName === 'Meeting' && meeting) {
      nextConditionalData.assignees = assigneeIdsFromLead;
      nextConditionalData.timeZone = meeting.time_zone || 'UTC';
      nextConditionalData.startDateTime = toDatetimeLocalValue(meeting.start_date_time);
      nextConditionalData.endDateTime = toDatetimeLocalValue(meeting.end_date_time);
      nextConditionalData.meetingDescription = meeting.description || '';
    }

    setNewStatus(statusId);
    setConditionalData(nextConditionalData);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setNewStatus('');
    setConditionalData({
      date: '',
      description: '',
      assignees: [],
      equipment_model_id: '',
      lease_id: '',
      merchant_service_provider_id: '',
      timeZone: 'UTC',
      startDateTime: '',
      endDateTime: '',
      meetingDescription: '',
    });
  };

  const handleConditionalChange = (field, value) => {
    setConditionalData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStatusChange = async () => {
    if (!lead?.id) return;
    if (!newStatus) {
      toast.warn('Select a status');
      return;
    }

    const statusObj = statusOptions.find((s) => s.id === newStatus);
    const statusName = statusObj?.name;
    const normalizedStatusName = String(statusName || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
    const payload = {
      status_id: newStatus,
      date: conditionalData.date,
      description: conditionalData.description,
    };

    if (statusName === 'Select') {
      if (!conditionalData.date || !conditionalData.description) {
        toast.warn('Date & description are required');
        return;
      }
    }

    if (statusName === 'Meeting') {
      if (
        !conditionalData.assignees?.length ||
        !conditionalData.startDateTime ||
        !conditionalData.endDateTime ||
        !conditionalData.meetingDescription
      ) {
        toast.warn('All meeting fields are required');
        return;
      }
      Object.assign(payload, {
        assignees: conditionalData.assignees,
        time_zone: conditionalData.timeZone,
        start_date_time: conditionalData.startDateTime,
        end_date_time: conditionalData.endDateTime,
        description: conditionalData.meetingDescription,
      });
    }

    // Floor Manager → Qualified: include selected closer assignee (same as lead list)
    if ((statusName || '').toLowerCase().trim() === 'qualified') {
      if (conditionalData.assignees?.length) {
        payload.assignees = conditionalData.assignees;
      }
    }

    // QC / QC Manager → QC Approved or QC Declined locks the lead
    if (isQcOrQcManagerRole(user) && isQcApprovedOrDeclinedStatus(statusName)) {
      payload.is_disabled = true;
    }

    if (
      normalizedStatusName.includes('submitted') &&
      normalizedStatusName.includes('equipment')
    ) {
      if (!conditionalData.equipment_model_id) {
        toast.warn('Please select equipment');
        return;
      }
      payload.equipment_model_id = conditionalData.equipment_model_id;
    }

    if (
      normalizedStatusName.includes('submitted') &&
      normalizedStatusName.includes('lease')
    ) {
      if (!conditionalData.lease_id) {
        toast.warn('Please select lease');
        return;
      }
      payload.lease_id = conditionalData.lease_id;
    }

    if (
      normalizedStatusName.includes('submitted') &&
      (normalizedStatusName.includes('(msp)') ||
        normalizedStatusName.includes('msp'))
    ) {
      if (!conditionalData.merchant_service_provider_id) {
        toast.warn('Please select merchant service provider');
        return;
      }
      payload.merchant_service_provider_id =
        conditionalData.merchant_service_provider_id;
    }

    try {
      setStatusProcessing(true);
      const res = await dispatch(updateLeadStatus({ leadId: lead.id, payload })).unwrap();
      if (res?.success) {
        toast.success('Status updated');
        handleCloseStatusDialog();
        fetchLeadDetail();
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to update status');
    } finally {
      setStatusProcessing(false);
    }
  };


  const getStatusColor = (statusName) => {
    const colorMap = {
      'New': 'primary',
      'Contacted': 'info',
      'Qualified': 'success',
      'Connected': 'warning',
      'Lost': 'error',
      'Follow up': 'secondary',
      'Chatting': 'info',
      'Select': 'default',
      'Meeting': 'warning',
      'Upcoming': 'info'
    };
    return colorMap[statusName] || 'default';
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString();
  };

  if (loading) {
    return (
      <PageLayout sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </PageLayout>
    );
  }

  if (!lead) {
    return (
      <PageLayout sx={{ justifyContent: 'center', alignItems: 'center' }}>
        <Typography>Lead not found</Typography>
      </PageLayout>
    );
  }

  const meetingsList = Array.isArray(lead.meetings) ? lead.meetings : [];
  const safeMeetingTab = Math.min(meetingTab, Math.max(0, meetingsList.length - 1));
  const activeMeeting = meetingsList[safeMeetingTab];
  const dba = lead.dba && typeof lead.dba === 'object' ? lead.dba : null;
  const dbaAddress = dba
    ? [dba.address, dba.city, dba.zip].map((part) => displayOrNa(part)).join(', ')
    : 'N/A';
  const equipmentsList = Array.isArray(lead.equipments)
    ? lead.equipments
    : Array.isArray(lead.equipment)
    ? lead.equipment
    : Array.isArray(dba?.equipments)
    ? dba.equipments
    : [];
  const offerServicesList = (
    Array.isArray(lead.offer_services)
      ? lead.offer_services
      : Array.isArray(dba?.offer_services)
        ? dba.offer_services
        : []
  ).filter((service) => {
    if (service == null) return false;
    if (typeof service === 'string') return Boolean(service.trim());
    const content =
      service?.service_content ?? service?.service_offered ?? '';
    return Boolean(String(content).trim());
  });

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header with Breadcrumbs */}
          <Box sx={{ mb: 4 }}>
            <Breadcrumbs sx={{ mb: 2 }}>
              <Link
                color="inherit"
                onClick={handleBack}
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Leads
              </Link>
              <Typography color="text.primary">Lead Details</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
                  {lead.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={lead.last_status_log?.status?.name}
                    color={getStatusColor(lead.last_status_log?.status?.name)}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Created: {formatDateTime(lead.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  variant="outlined"
                >
                  Back to Leads
                </Button>
                <Can permission="lead-status">
                  {!isLeadDisabledLike(lead, user) && (
                  <Button
                    variant="contained"
                    onClick={handleOpenStatusDialog}
                    disabled={statusProcessing}
                  >
                    Change Status
                  </Button>
                  )}
                </Can>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Left Column - Lead Information */}
            <Grid item xs={12} md={8}>
              {/* Basic Information Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon />
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {lead.name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        DBA Name
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {lead.dba_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Email
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {lead.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Phone
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {lead.phone || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {lead.description || 'No description provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Additional note
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {displayOrNa(lead.additional_note ?? dba?.additional_note)}
                      </Typography>
                    </Grid>
                     <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Country
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {lead.country?.name || 'No country provided'}
                      </Typography>
                    </Grid>
                     <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created By
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between' }}>
                       <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> {lead.author?.name || 'No author provided'}-({lead?.author?.email || 'not found'})</Typography>  
                         {/* <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> 
                         
                        </Typography> */}
                      </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created At
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between' }}>
                       <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> {lead?.created_at || 'No author provided'}</Typography>  
                         {/* <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> 
                        {lead?.created_at || 'not found'} 
                        </Typography> */}
                      </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between' }}>
                       <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> {lead?.address || 'No author provided'}</Typography>  
                         {/* <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', }}> 
                        {lead?.address || 'not found'} 
                        </Typography> */}
                      </Box>
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {dba && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <BusinessIcon />
                      DBA Information
                    </Typography>
                    <Grid container spacing={2}>
                      <DbaDetailField label="DBA Name" value={dba.name} />
                      <DbaDetailField label="Email" value={dba.email} />
                      <DbaDetailField label="Phone" value={dba.phone} />
                      <DbaDetailField label="Fax Phone" value={dba.fax_phone} />
                      <DbaDetailField label="SSN Number" value={dba.ssn} />
                      <DbaDetailField label="Address" value={dbaAddress} md={12} />
                      <DbaDetailField
                        label="Submission date"
                        value={dba.submission_date ? formatDateTime(dba.submission_date) : 'N/A'}
                      />
                      <DbaDetailField label="Monthly volume" value={dba.monthly_volume} />
                      <DbaDetailField label="Monthly lease" value={dba.monthly_lease} />
                      <DbaDetailField label="Is bundled" value={formatBundledDisplay(dba.is_bundled)} />
                      <DbaDetailField label="Processing offered" value={dba.processing_offered} />
                      <DbaDetailField label="Highest ticket" value={dba.highest_ticket} />
                      <DbaDetailField label="FNS number for EBT" value={dba.fns_number_for_ebt} />
                      <DbaDetailField
                        label="Created At"
                        value={dba.created_at ? formatDateTime(dba.created_at) : 'N/A'}
                      />
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Equipment Card */}
              {equipmentsList.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <DevicesOtherIcon />
                      Equipment ({equipmentsList.length})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Manufacturer</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Tracking ID</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Lease</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Merchant Service Provider</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {equipmentsList.map((eq, index) => (
                            <TableRow key={eq?.id ?? index}>
                              <TableCell>{eq?.equipment_type?.name || 'N/A'}</TableCell>
                              <TableCell>{eq?.manufacturer?.name || 'N/A'}</TableCell>
                              <TableCell>{eq?.equipment_model?.name || 'N/A'}</TableCell>
                              <TableCell>{eq?.tracking_id || 'N/A'}</TableCell>
                              <TableCell>{eq?.sku_number || 'N/A'}</TableCell>
                              <TableCell>{eq?.lease?.name || 'N/A'}</TableCell>
                              <TableCell>{eq?.merchant_service_provider?.name || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Offered Services Card */}
              {offerServicesList.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <MiscellaneousServicesIcon />
                      Offered Services ({offerServicesList.length})
                    </Typography>
                    <List disablePadding>
                      {offerServicesList.map((service, index) => (
                        <ListItem
                          key={service?.id ?? index}
                          alignItems="flex-start"
                          sx={{
                            px: 0,
                            py: 1.5,
                            borderBottom:
                              index < offerServicesList.length - 1
                                ? '1px solid'
                                : 'none',
                            borderColor: 'divider',
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, whiteSpace: 'pre-wrap', mb: 0.5 }}
                              >
                                {typeof service === 'string'
                                  ? service
                                  : service?.service_content ||
                                    service?.service_offered ||
                                    'N/A'}
                              </Typography>
                            }
                            secondary={
                              service?.created_at
                                ? `Created: ${service.created_at}`
                                : null
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

              {/* Status History Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon />
                    Current Status
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Current Status
                      </Typography>
                      <Chip
                        label={lead.last_status_log?.status?.name}
                        color={getStatusColor(lead.last_status_log?.status?.name)}
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Status Description
                      </Typography>
                      <Typography variant="body1">
                        {lead.last_status_log?.description || 'No description'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Status Created
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTime(lead.last_status_log?.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Created By
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {lead.last_status_log?.author?.name?.charAt(0) || 'U'}
                        </Avatar>
                        <Typography variant="body1">
                          {lead.last_status_log?.author?.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Meetings (multiple) — from lead.meetings */}
              {meetingsList.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon />
                      Meetings ({meetingsList.length})
                    </Typography>
                    <Tabs
                      value={safeMeetingTab}
                      onChange={(_, v) => setMeetingTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                      {meetingsList.map((m, i) => (
                        <Tab
                          key={m.id || i}
                          label={
                            m.start_date_time
                              ? `Meeting ${i + 1} · ${formatDateTime(m.start_date_time)}`
                              : `Meeting ${i + 1}`
                          }
                        />
                      ))}
                    </Tabs>
                    {activeMeeting && (
                      <Box role="tabpanel">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                          {activeMeeting.id && (
                            <>
                              <Button variant="outlined" size="small" onClick={() => navigate(`${MEETING_VIEW_PATH}/${activeMeeting.id}`)}>
                                View
                              </Button>
                              <Button variant="contained" size="small" onClick={() => navigate(`${MEETING_EDIT_PATH}/${activeMeeting.id}`)}>
                                Edit
                              </Button>
                            </>
                          )}
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimeIcon fontSize="small" />
                                Start
                              </Box>
                            </Typography>
                            <Typography variant="body1">{formatDateTime(activeMeeting.start_date_time)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimeIcon fontSize="small" />
                                End
                              </Box>
                            </Typography>
                            <Typography variant="body1">{formatDateTime(activeMeeting.end_date_time)}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LanguageIcon fontSize="small" />
                                Time zone
                              </Box>
                            </Typography>
                            <Typography variant="body1">{activeMeeting.time_zone || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Created
                            </Typography>
                            <Typography variant="body1">{formatDateTime(activeMeeting.created_at)}</Typography>
                          </Grid>
                          {activeMeeting.google_event_url && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Google Meet
                              </Typography>
                              <Link href={activeMeeting.google_event_url} target="_blank" rel="noopener noreferrer">
                                {activeMeeting.google_event_url}
                              </Link>
                            </Grid>
                          )}
                          {(activeMeeting.google_sync_status || activeMeeting.google_error_message) && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Google sync
                              </Typography>
                              <Chip
                                size="small"
                                label={activeMeeting.google_sync_status || '—'}
                                color={activeMeeting.google_sync_status === 'failed' ? 'warning' : 'default'}
                                sx={{ mr: 1 }}
                              />
                              {activeMeeting.google_error_message && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {activeMeeting.google_error_message}
                                </Typography>
                              )}
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Description
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                              {activeMeeting.description || '—'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Legacy: single meeting from last status log (when lead.meetings is empty) */}
              {meetingsList.length === 0 && lead.last_status_log?.meeting && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon />
                      Meeting Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" />
                            Start Time
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(lead.last_status_log.meeting.start_date_time)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TimeIcon fontSize="small" />
                            End Time
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(lead.last_status_log.meeting.end_date_time)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LanguageIcon fontSize="small" />
                            Time Zone
                          </Box>
                        </Typography>
                        <Typography variant="body1">
                          {lead.last_status_log.meeting.time_zone}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Meeting Status
                        </Typography>
                        <Chip
                          label={lead.last_status_log.status?.name}
                          color={getStatusColor(lead.last_status_log.status?.name)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Meeting Description
                        </Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                          {lead.last_status_log.meeting.description || 'No meeting description'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Attachments Card */}
              <Can permission="attachment-list">
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <AttachmentIcon />
                    Attachments ({lead.attachments?.length || 0})
                  </Typography>

                  {lead.attachments && lead.attachments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>File Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Action</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {lead.attachments.map((file) => (
                            <TableRow
                              key={file.id}
                              hover={canViewAttachment && file.type?.startsWith('image/')}
                              sx={{
                                cursor: canViewAttachment && file.type?.startsWith('image/') ? 'pointer' : 'default',
                              }}
                              onClick={
                                canViewAttachment && file.type?.startsWith('image/')
                                  ? () => handleImageClick(file)
                                  : undefined
                              }
                            >
                              {/* File Name */}
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DescriptionIcon color="action" />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 200,
                                    }}
                                    title={file.title}
                                  >
                                    {file.title}
                                  </Typography>
                                </Box>
                              </TableCell>

                              {/* File Type */}
                              <TableCell>
                                <Chip
                                  label={file.type?.split('/')[1]?.toUpperCase() || 'FILE'}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>

                              {/* Action Buttons */}
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Can permission="attachment-view">
                                  <IconButton
                                    size="small"
                                    href={file.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Can>
                                <Can permission="attachment-download">
                                  <IconButton
                                    size="small"
                                    disabled={downloadingAttachmentId != null}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadFile(file);
                                    }}
                                  >
                                    {downloadingAttachmentId === (file?.id ?? file?.path) ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <DownloadIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Can>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 3 }}
                    >
                      No attachments found
                    </Typography>
                  )}
                </CardContent>
              </Card>
              </Can>

            </Grid>

            {/* Right Column - Side Information */}
            <Grid item xs={12} md={4}>
              {/* Source & Brand Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SourceIcon />
                    Source & Brand
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <SourceIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Source"
                        secondary={lead.source?.name || 'Not specified'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <BusinessIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Brand"
                        secondary={lead.brand?.name || 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              {/* Assignees Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon />
                    Assignee
                  </Typography>
                  {/* {lead?.last_status_log?.assignee && lead?.last_status_log?.assignee.length > 0 ? ( */}
                  <List dense>
                    {(lead?.assignees ?? []).map((assignee, index) => (
                    <ListItem key={assignee?.id ?? index}>
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                          {assignee?.name?.charAt(0) || 'U'}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={assignee?.name ?? 'Unknown'}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {assignee?.email ?? '—'}
                            </Typography>
                            {assignee?.phone && (
                              <Typography variant="body2" color="text.secondary">
                                {assignee.phone}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    ))}
                  </List>
                  {/* ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No assignees
                    </Typography>
                  )} */}
                </CardContent>
              </Card>

              {/* Status Assignee Card */}
              {/* {lead.status?.assignee && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon />
                      Status Assignee
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 48, height: 48 }}>
                        {lead.status.assignee.name?.charAt(0) || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {lead.status.assignee.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.status.assignee.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {lead.status.assignee.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )} */}

              {/* DBA Information Card */}
              {/* <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon />
                    DBA Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {lead.dba ? 'DBA details available' : 'No DBA associated'}
                  </Typography>
                  {lead.dba && (
                    <Button variant="outlined" fullWidth
                      onClick={() => navigate(`/view-merchant?id=${lead?.dba?.id}`)}
                    >
                      View DBA Details
                    </Button>
                  )}
                </CardContent>
              </Card> */}
            </Grid>
          </Grid>
      </Container>

      {/* Image Preview Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Image Preview</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={selectedImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImageDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => handleDownloadFile(selectedImageFile)}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      <StatusDialog
        open={statusDialogOpen}
        selectedLead={lead}
        newStatus={newStatus}
        conditionalData={conditionalData}
        statusOptions={statusOptions}
        usersList={frontSellerOptions}
        processingId={statusProcessing ? lead?.id : null}
        onClose={handleCloseStatusDialog}
        onStatusChange={handleStatusChange}
        onNewStatusChange={setNewStatus}
        onConditionalChange={handleConditionalChange}
      />
    </PageLayout>
  );
};

export default LeadDetail;